// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { BallTriangle, ThreeDots } from 'react-loading-icons'
import Select from 'react-select'
import toast from 'react-simple-toasts'

import { adminEmployers, adminRecordInspector } from 'rails_routes'
import { TextLink } from 'slab'

import {
  type Employee,
  type EmployeesByEmployeeId,
  type Employer,
  type FinchConnectionStatus,
} from '../types'
import {
  beginningOfMonth,
  endOfMonth,
  formatDate,
  getFormattedDate,
  isValidDateInput,
  toastOptions,
  toasttOptionsError,
} from '../utils'
import FinchApiOutput from './FinchApiOutput'
import FinchDedupeEmployees from './FinchDedupeEmployees'
import Badge from './reusable/Badge'
import ButtonPrimary from './reusable/ButtonPrimary'
import Dropdown, { SelectItem } from './reusable/Dropdown'
import Tab from './reusable/Tab'
import { Input } from './ui/input'
import { Switch } from './ui/switch'

type Endpoint =
  | 'introspect'
  | 'directory'
  | 'individual'
  | 'employment'
  | 'payment'
  | 'pay_statement'
  | 'benefits'
  | 'pay_groups'

type FinchApiExplorerProps = {
  employer_id: string
}

type SelectOption = {
  label: string
  value: string
}

type DropdownOption = {
  outputValue: string
  employeeRequired?: boolean
  dateRangeRequired?: boolean
  paymentIdRequired?: boolean
  payGroupIdRequired?: boolean
}

type FinchData = {
  introspect: any
  directory: any
  individual: any
  employment: any
  payment: any
  pay_statement: any
  benefits: any
  pay_groups: any
  pay_group: any
}

type TabOptions = 'api' | 'dedupe'

const finchConnectionStatusDenormalized: { [key: string]: string } = {
  connected: 'Connected',
  paused: 'Paused',
  pending_disconnection: 'Pending Disconnection',
}

const defaultFinchData: FinchData = {
  introspect: null,
  directory: null,
  individual: null,
  employment: null,
  payment: null,
  pay_statement: null,
  benefits: null,
  pay_groups: null,
  pay_group: null,
}

const defaultEmployerData: Employer = {
  id: '',
  payroll_config: {
    id: '',
    deduction_method: 'consistent',
    payroll_finch_config: {
      id: '',
      connection_id: '',
      client_id: '',
      finch_provider_id: '',
      finch_connection_status: 'connected',
      last_employee_sync: '',
      created_at: '',
    },
  },
  profile: {
    id: '',
    name: '',
    dba: '',
    number_of_employees: 0,
  },
}

const BASE_FINCH_URL = '/api/web/admin/finch'

const options: { [key: string]: DropdownOption } = {
  introspect: {
    outputValue: 'Introspect',
  },
  directory: {
    outputValue: 'Directory',
  },
  individual: {
    outputValue: 'Individual',
    employeeRequired: true,
  },
  employment: {
    outputValue: 'Employment',
    employeeRequired: true,
  },
  payment: {
    outputValue: 'Payment',
    dateRangeRequired: true,
  },
  pay_statement: {
    outputValue: 'Pay Statement',
    paymentIdRequired: true,
  },
  benefits: {
    outputValue: 'Benefits',
  },
  pay_groups: {
    outputValue: 'Pay Groups',
  },
  pay_group: {
    outputValue: 'Pay Group',
    payGroupIdRequired: true,
  }
}

function FinchApiExplorer ({
  employer_id: employerIdFromRoute,
}: FinchApiExplorerProps) {
  const [employerId, setEmployerId] = useState(employerIdFromRoute)
  const [employer, setEmployer] = useState<Employer>(defaultEmployerData)
  const [finchData, setFinchData] = useState<FinchData>(defaultFinchData)
  const [endpoint, setEndpoint] = useState<Endpoint>('individual')
  const [employees, setEmployees] = useState<EmployeesByEmployeeId>({})
  const [selectedEmployees, setSelectedEmployees] = useState<
    readonly SelectOption[]
  >([])
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<
    readonly SelectOption[]
  >([])
  const [selectedPayGroupId, setSelectedPayGroupId] = useState<SelectOption | null>()

  const [activeTab, setActiveTab] = useState<TabOptions>('api')

  const currentDate = new Date()
  const [startDate, setStartDate] = useState<string | undefined>(
    formatDate(
      beginningOfMonth(currentDate.getMonth(), currentDate.getFullYear())
    )
  )
  const [endDate, setEndDate] = useState<string | undefined>(
    formatDate(endOfMonth(currentDate.getMonth(), currentDate.getFullYear()))
  )

  const [isEmployerLoading, setIsEmployerLoading] = useState(true)
  const [isEmployeesDataLoading, setIsEmployeesDataLoading] = useState(true)
  const [isFinchDataLoading, setIsFinchDataLoading] = useState(false)
  const [isUpdatingFinchConnectionStatus, setIsUpdatingFinchConnectionStatus] =
    useState(false)

  const [employerError, setEmployerError] = useState(null)
  const [error, setError] = useState(null)

  const [isFetchIndividualIdEnabled, setIsFetchIndividualIdEnabled] =
    useState(false)
  const [finchIndividualId, setFinchIndividualId] = useState<string>('')

  const { employeeRequired, dateRangeRequired, paymentIdRequired, payGroupIdRequired } =
    options[endpoint]

  useEffect(() => {
    setEmployerId(employerIdFromRoute)
  }, [employerIdFromRoute])

  const fetchEmployerById = async (id: string) => {
    try {
      const params = new URLSearchParams([['employer_id', id]]).toString()
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(`${BASE_FINCH_URL}/employer?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      const json = await response.json()
      if (response.status !== 200) {
        throw new Error(`Error loading employer ${employerId}: ${json.message}`)
      }
      setIsEmployerLoading(false)
      setEmployer(json.employer)
      fetchEmployeesForEmployer(json.employer.id)
    } catch (e: any) {
      setIsEmployerLoading(false)
      setEmployerError(e.message)
      console.error(e)
    }
  }

  const fetchEmployeesForEmployer = async (id: string) => {
    try {
      const params = new URLSearchParams([['employer_id', id]]).toString()
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(
        `${BASE_FINCH_URL}/employer/employees?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const json = await response.json()
      if (response.status !== 200) {
        throw new Error(
          `Error fetching employees endpoint for ${employerId}: ${json.message}`
        )
      }
      setIsEmployeesDataLoading(false)
      setEmployees(
        json.employees.reduce(
          (acc: EmployeesByEmployeeId, employee: Employee) => ({
            ...acc,
            [employee.id]: employee,
          }),
          {}
        )
      )
    } catch (e: any) {
      setIsEmployeesDataLoading(false)
      setError(e.message)
      console.error(e)
    }
  }

  const handleFinchRequest = async () => {
    let baseParams = [['employer_id', employer?.id]]

    if (employeeRequired && !isFetchIndividualIdEnabled && selectedEmployees) {
      baseParams = [
        ...baseParams,
        ...selectedEmployees.map(({ value }) => ['finch_ids[]', value]),
      ]
    }

    if (employeeRequired && isFetchIndividualIdEnabled && finchIndividualId) {
      baseParams = [...baseParams, ...[['finch_ids[]', finchIndividualId]]]
    }

    if (dateRangeRequired && startDate && endDate) {
      baseParams = [
        ...baseParams,
        ['start_date', startDate],
        ['end_date', endDate],
      ]
    }

    if (paymentIdRequired && selectedPaymentIds) {
      baseParams = [
        ...baseParams,
        ...selectedPaymentIds.map(({ value }) => ['payment_ids[]', value]),
      ]
    }

    if (payGroupIdRequired && selectedPayGroupId) {
      baseParams = [...baseParams, ['pay_group_id', selectedPayGroupId.value]]
    }

    const params = new URLSearchParams(baseParams).toString()

    try {
      setIsFinchDataLoading(true)
      setError(null)
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const response = await fetch(
        `${BASE_FINCH_URL}/api/${endpoint}?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const json = await response.json()
      if (response.status !== 200) {
        throw new Error(
          `Error fetching ${endpoint} endpoint ${employerId}: ${json.message}`
        )
      }
      setIsFinchDataLoading(false)
      setFinchData((prevState) => ({ ...prevState, [endpoint]: json.output }))
    } catch (e: any) {
      setIsFinchDataLoading(false)
      setError(e.message)
      console.error(e)
    }
  }

  const handleSyncEmployerRequest = async () => {
    try {
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
    // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'post',
        `${BASE_FINCH_URL}/employer/sync`,
        {
          body: {
            employer_id: employer?.id,
          },
        }
      )

      const { response } = await request.perform()
      const json = await response.json

      if (response.status !== 201) {
        throw new Error(
          `Error syncing employer directory with Finch ${employerId}: ${json.message}`
        )
      }
      toast(
        'Syncing employer directory with Finch! This can take a few minutes to complete.',
        toastOptions
      )
    } catch (e: any) {
      console.error(e)
    }
  }

  const setFinchConnectionStatus = (status: FinchConnectionStatus) => {
    setEmployer((prevState) => ({
      ...prevState,
      payroll_config: {
        ...prevState.payroll_config,
        finch_connection_status: status,
      },
    }))
  }

  const finchConnectionStatus: FinchConnectionStatus =
    employer?.payroll_config.payroll_finch_config?.finch_connection_status || 'connected'

  useEffect(() => {
    setIsUpdatingFinchConnectionStatus(false)
  }, [finchConnectionStatus])

  const handleUpdateFinchConnectionStatus = async (
    status: FinchConnectionStatus
  ) => {
    try {
      setIsUpdatingFinchConnectionStatus(true)
      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'put',
        `${BASE_FINCH_URL}/employer/update_finch_connection_status`,
        {
          body: {
            employer_id: employer?.id,
            new_connection_status: status,
          },
        }
      )
      const { response } = await request.perform()
      const json = await response.json

      if (response.status !== 200) {
        throw new Error(
          `Error updating the Finch connection status ${employerId}: ${json.message}`
        )
      }
      setFinchConnectionStatus(status)
      toast('Finch connection status updated!', toastOptions)
    } catch (e: any) {
      toast(`Finch connection update failed! ${e.message}`, {
        ...toastOptions,
        ...toasttOptionsError,
      })
      setIsUpdatingFinchConnectionStatus(false)
      console.error(e)
    }
  }

  useEffect(() => {
    fetchEmployerById(employerId)
  }, [employerId])

  if (isEmployerLoading) {
    return (
      <div className='w-full flex flex-col items-center'>
        <div className='italic text-xl'>Loading Finch API Explorer...</div>
        <BallTriangle
          stroke='#4f46e5'
          style={{ width: '100px', height: '200px' }}
        />
      </div>
    )
  }

  if (employerError) {
    return <div>{employerError}</div>
  }

  if (!employer) {
    return <div>Employer not found</div>
  }

  const employeesOptions: SelectOption[] = Object.values(employees).map(
    (employee) => ({
      label: `${employee.first_name} ${employee.last_name}`,
      value: employee.integration_employee_id,
    })
  )

  const isFetchDisabled =
    (employeeRequired &&
      (isEmployeesDataLoading ||
        (isFetchIndividualIdEnabled
          ? finchIndividualId.length === 0
          : selectedEmployees.length === 0))) ||
    (dateRangeRequired &&
      (!isValidDateInput(startDate || '') ||
        !isValidDateInput(endDate || ''))) ||
    (paymentIdRequired && selectedPaymentIds.length === 0) ||
    (payGroupIdRequired && !selectedPayGroupId)

  // TODO: Make this component more reusable by extracting the JSX into a separate component.

  if (activeTab === 'dedupe') {
    return (
      <NavigationTabs
        employer={employer}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      >
        <FinchDedupeEmployees
          employees={employees}
          employer={employer}
          isEmployeesDataLoading={isEmployeesDataLoading}
          fetchEmployeesForEmployer={fetchEmployeesForEmployer}
        />
      </NavigationTabs>
    )
  }

  return (
    <NavigationTabs
      employer={employer}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    >
      <div className='flex'>
        <div className='w-9/12 flex flex-col justify-between p-2'>
          <div>
            <div className='flex my-4'>
              <div className='mr-6' style={{ width: '12rem' }}>
                <Dropdown<DropdownOption, Endpoint>
                  label='Finch API Endpoint:'
                  value={endpoint}
                  outputValue={options[endpoint].outputValue}
                  options={Object.entries(options)}
                  handleSetValue={setEndpoint}
                  placeholder='Select an endpoint...'
                  optionsMapCallback={([key, value]) => {
                    return (
                      <SelectItem
                        key={key}
                        value={key}
                        outputValue={value.outputValue}
                        currentValue={endpoint}
                      />
                    )
                  }}
                />
              </div>
              <div className='self-end'>
                <ButtonPrimary
                  onClick={() => handleFinchRequest()}
                  isDisabled={isFetchDisabled}
                  testId='fetch'
                >
                  Fetch
                </ButtonPrimary>
              </div>
              {employeeRequired && (
                <div className='self-end pl-4'>
                  <div className='flex items-center py-2'>
                    <Switch
                      checked={isFetchIndividualIdEnabled}
                      onCheckedChange={setIsFetchIndividualIdEnabled}
                    />
                    <span className='text-sm font-semibold pl-2'>
                      Fetch Individual ID
                    </span>
                  </div>
                </div>
              )}
            </div>
            {employeeRequired &&
              !isFetchIndividualIdEnabled &&
              (isEmployeesDataLoading
                ? (
                  <div className='flex items-center'>
                    <ThreeDots
                      stroke='#4f46e5'
                      fill='#4f46e5'
                      style={{ width: '40px', height: '16px' }}
                    />
                    <div className='ml-2'>Loading employees</div>
                  </div>
                  )
                : (
                  <div data-testid='employee-select'>
                    <Select
                      isMulti
                      value={selectedEmployees}
                      options={employeesOptions}
                      onChange={setSelectedEmployees}
                      inputId='select-input'
                      instanceId='dropdown'
                    />
                  </div>
                  ))}
            {employeeRequired && isFetchIndividualIdEnabled && (
              <div>
                <Input
                  placeholder='Paste individual ID here'
                  value={finchIndividualId}
                  onChange={(e) =>
                    setFinchIndividualId(
                      e.target.value.replace(/^['"](.*)['"]$/, '$1')
                    )}
                />
              </div>
            )}
            {dateRangeRequired && (
              <div className='flex'>
                <div>
                  <div className='text-sm italic py-2'>Start Date:</div>
                  <input
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder='YYYY-MM-DD'
                    className='border rounded-md p-2'
                  />
                </div>
                <div className='ml-4'>
                  <div className='text-sm italic py-2'>End Date:</div>
                  <input
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder='YYYY-MM-DD'
                    className='border rounded-md p-2'
                  />
                </div>
              </div>
            )}
            {paymentIdRequired &&
              (!finchData.payment
                ? (
                  <div className='text-sm italic py-2 text-red-500'>
                    Please fetch payment endpoint information to populate list of
                    available payments
                  </div>
                  )
                : (
                  <div data-testid='payment-select'>
                    <Select
                      isMulti
                      value={selectedPaymentIds}
                      options={finchData.payment.map(
                        ({ id, pay_period: payPeriod }: any) => ({
                          label: `${payPeriod.start_date} to ${payPeriod.end_date}`,
                          value: id,
                        })
                      )}
                      onChange={setSelectedPaymentIds}
                      inputId='select-input'
                      instanceId='dropdown'
                    />
                  </div>
                  ))}
            {payGroupIdRequired &&
              (!finchData.pay_groups
                ? (
                  <div className='text-sm italic py-2 text-red-500'>
                    Please fetch pay group endpoint information to populate list of
                    available pay groups
                  </div>
                  )
                : (
                  <div data-testid='pay-group-select'>
                    <Select
                      value={selectedPayGroupId}
                      options={finchData.pay_groups.map(
                        ({ id, name }: any) => ({
                          label: name,
                          value: id,
                        })
                      )}
                      onChange={setSelectedPayGroupId}
                      inputId='pay-group-select-input'
                      instanceId='pay-group-dropdown'
                    />
                  </div>
                  ))}
          </div>
        </div>
        <div
          style={{ minWidth: '100px' }}
          className='w-3/12 flex flex-col items-center p-2 justify-between'
        >
          <div
            style={{ transition: 'all 0.15s ease', width: '12rem' }}
            className='my-2'
          >
            <Dropdown<string, FinchConnectionStatus>
              label='Connection Status:'
              value={finchConnectionStatus}
              outputValue={
                finchConnectionStatusDenormalized[finchConnectionStatus]
              }
              options={Object.entries(finchConnectionStatusDenormalized)}
              handleSetValue={handleUpdateFinchConnectionStatus}
              placeholder='Select a connection status...'
              isLoading={isUpdatingFinchConnectionStatus}
              isDisabled={finchConnectionStatus === 'disconnected'}
              optionsMapCallback={([key, value]) => {
                return (
                  <SelectItem
                    key={key}
                    value={key}
                    outputValue={value}
                    currentValue={endpoint}
                    isDisabled={key === 'disconnected'}
                  />
                )
              }}
            />
          </div>
          <div className='text-sm italic my-2'>Last Employee Sync</div>
          {employer?.payroll_config.payroll_finch_config?.last_employee_sync
            ? (
              <Badge
                text={getFormattedDate(
                  employer?.payroll_config.payroll_finch_config?.last_employee_sync,
                  { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' }
                )}
                type='success'
              />
              )
            : (
              <Badge text='Awaiting initial sync...' type='warning' />
              )}

          <div className='mt-2'>
            <ButtonPrimary onClick={() => handleSyncEmployerRequest()}>
              Sync Employer
            </ButtonPrimary>

            <div className='mt-2'>
              <div
                className={clsx({
                  'inline-block rounded-md py-2 px-4 mt-2 md:mt-0 text-center text-md font-medium shadow-sm border':
                    true,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2':
                    true,
                  'h-10 flex justify-center align-center': true,
                  'bg-gray-200 text-gray-600 cursor-pointer border-gray-300 hover:border-gray-600 hover:bg-gray-600 hover:text-white':
                    true,
                })}
                style={{ transition: 'all 0.15s ease', width: '12rem' }}
                onClick={() =>
                  window.open(
                    `https://dashboard.tryfinch.com/app/applications/${employer?.payroll_config.payroll_finch_config?.client_id}/connections/${employer?.payroll_config.payroll_finch_config?.connection_id}`,
                    '_blank'
                  )}
              >
                Finch Dashboard
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <FinchApiOutput
          isLoading={isFinchDataLoading}
          data={finchData[endpoint]}
          error={error || ''}
        />
      </div>
    </NavigationTabs>
  )
}

const NavigationTabs = ({
  employer,
  activeTab,
  setActiveTab,
  children,
}: {
  employer: Employer
  activeTab: TabOptions
  setActiveTab: React.Dispatch<React.SetStateAction<TabOptions>>
  children: React.ReactNode
}) => {
  return (
    <div>
      <div className='text-center text-2xl mb-4'>Finch Cerebro</div>

      <div className='mb-4 flex justify-between items-end'>
        <TextLink
          href={adminEmployers.show({ id: employer?.id })}
        >
          <span className='text-xl'>{employer?.profile.name} </span>
          <span className='text-lg italic'>
            {employer?.profile.dba && `(dba ${employer?.profile.dba})`}
          </span>
        </TextLink>
        <TextLink
          href={adminRecordInspector.show({ id: employer?.payroll_config.payroll_finch_config?.id })}
        >
          <span className='text-sm'>{employer?.payroll_config.payroll_finch_config?.id} </span>
        </TextLink>
      </div>
      <div className='flex justify-between rounded-tl-md rounded-tr-md h-12'>
        <Tab
          width='49%'
          activeTab={activeTab}
          value='api'
          setActiveTab={setActiveTab}
        >
          Finch API Explorer
        </Tab>
        <Tab
          width='49%'
          activeTab={activeTab}
          value='dedupe'
          setActiveTab={setActiveTab}
        >
          Dedupe Employees
        </Tab>
      </div>
      {children}
    </div>
  )
}

export default FinchApiExplorer
